import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface VendorBenchmark {
  vendor_name_normalized: string;
  category: string | null;
  sample_count: number;
  avg_transaction_amount: number;
  median_transaction_amount: number;
  min_transaction_amount: number;
  max_transaction_amount: number;
  stddev_transaction_amount: number | null;
  avg_amount_30d: number | null;
  avg_amount_90d: number | null;
  price_trend_pct: number | null;
}

interface FraudPattern {
  pattern_type: string;
  amount_range_min: number;
  amount_range_max: number;
  detection_count: number;
  confidence_score: number;
}

interface IndustryBenchmark {
  company_size_bucket: string;
  industry_category: string | null;
  spending_category: string;
  sample_count: number;
  avg_monthly_spend: number;
  median_monthly_spend: number;
  percentile_25: number;
  percentile_75: number;
}

interface VendorComparison {
  vendorName: string;
  yourAvg: number;
  marketAvg: number;
  marketMedian: number;
  difference: number;
  differencePercent: number;
  isAboveMarket: boolean;
  trend: number | null;
  sampleSize: number;
}

// Normalize vendor name for matching
function normalizeVendorName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function useVendorBenchmarks() {
  return useQuery({
    queryKey: ["vendor-benchmarks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vendor_benchmarks")
        .select("*")
        .gte("sample_count", 5)
        .order("sample_count", { ascending: false });

      if (error) throw error;
      return data as VendorBenchmark[];
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function useFraudPatterns() {
  return useQuery({
    queryKey: ["fraud-patterns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fraud_corpus")
        .select("*")
        .gte("detection_count", 3)
        .gte("confidence_score", 50)
        .order("confidence_score", { ascending: false });

      if (error) throw error;
      return data as FraudPattern[];
    },
    staleTime: 1000 * 60 * 30,
  });
}

export function useIndustryBenchmarks(sizeBucket?: string, industry?: string) {
  return useQuery({
    queryKey: ["industry-benchmarks", sizeBucket, industry],
    queryFn: async () => {
      let query = supabase
        .from("industry_benchmarks")
        .select("*")
        .gte("sample_count", 10);

      if (sizeBucket) {
        query = query.eq("company_size_bucket", sizeBucket);
      }
      if (industry) {
        query = query.eq("industry_category", industry);
      }

      const { data, error } = await query.order("spending_category");

      if (error) throw error;
      return data as IndustryBenchmark[];
    },
    staleTime: 1000 * 60 * 30,
  });
}

export function useVendorComparison(userInvoices: Array<{ vendor: string; amount: number }>) {
  const { data: benchmarks } = useVendorBenchmarks();

  if (!benchmarks || userInvoices.length === 0) {
    return { comparisons: [], isLoading: !benchmarks };
  }

  // Group user invoices by vendor
  const userVendorSpend = new Map<string, number[]>();
  for (const inv of userInvoices) {
    const normalized = normalizeVendorName(inv.vendor);
    if (!userVendorSpend.has(normalized)) {
      userVendorSpend.set(normalized, []);
    }
    userVendorSpend.get(normalized)!.push(inv.amount);
  }

  // Create benchmark map
  const benchmarkMap = new Map<string, VendorBenchmark>();
  for (const b of benchmarks) {
    benchmarkMap.set(b.vendor_name_normalized, b);
  }

  // Compare
  const comparisons: VendorComparison[] = [];
  for (const [vendorNorm, amounts] of userVendorSpend) {
    const benchmark = benchmarkMap.get(vendorNorm);
    if (!benchmark) continue;

    const yourAvg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
    const marketAvg = benchmark.avg_transaction_amount;
    const difference = yourAvg - marketAvg;
    const differencePercent = (difference / marketAvg) * 100;

    comparisons.push({
      vendorName: vendorNorm,
      yourAvg,
      marketAvg,
      marketMedian: benchmark.median_transaction_amount,
      difference,
      differencePercent,
      isAboveMarket: difference > 0,
      trend: benchmark.price_trend_pct,
      sampleSize: benchmark.sample_count,
    });
  }

  // Sort by absolute difference
  comparisons.sort((a, b) => Math.abs(b.differencePercent) - Math.abs(a.differencePercent));

  return { comparisons, isLoading: false };
}

export function useCompanyProfile() {
  return useQuery({
    queryKey: ["company-profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("company_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });
}

export async function updateCompanyProfile(profile: {
  company_size_bucket?: string;
  industry_category?: string;
  region?: string;
  annual_revenue_bucket?: string;
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("company_profiles")
    .upsert({
      user_id: user.id,
      ...profile,
      updated_at: new Date().toISOString(),
    }, {
      onConflict: "user_id",
    });

  if (error) throw error;
}
